import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { apiFetch } from '../apiClient';
import { projectPath, useSelectedProject, usePruneMissingProject } from './projectDeepLink';
import { ErrorBanner, SortTh, lower, rowClassName, sortRows, useGridSort } from './Configuration';
import { useRegisterDirty } from '../unsavedChangesGuard';
import { parseEstimateWorkbook } from './budgetEstimateImport';
import { MultiSelect } from './registerFilters';
import { isDone, periodDateOf } from './milestoneTrackerLogic';
import { useRememberedPeriod } from './rememberedPeriod';

// Team/project-level budget maintenance (item 6, Jeremy 2026-09-09) -- pulled forward ahead of
// Financials' December roadmap slot, same precedent as bpa/risk-register. Not POC-scoped like the
// customer-facing registers: budget/hours tracking is an internal PM/finance concern regardless of
// contract type. Actual $ stays manually entered (no invoiced-cost source yet) but isn't shown on
// this grid (Jeremy, 2026-09-28) -- it's only ever meaningful at the rollup level for now. Actual
// hours are only ever shown at the team/project rollup level, never per line -- TimeEntry isn't
// tagged by budget category, so a per-line actual would be a guess dressed up as a fact.
//
// Category is a fixed, admin-maintained pick list (BudgetCategory, Jeremy 2026-09-28) rather than
// free text -- OnePlan's cost categories were never the real source for this data, so there's
// nothing worth preserving by keeping it a string.
//
// Grid follows the register convention (Configuration.js's rowClassName/.sheet-toolbar, ADR 0040):
// cells edit in place, a row is dirtied rather than saved individually, and one "Save N change(s)"
// button flushes everything at once.
const BLANK_LINE = { plannedAmount: 0 };

// Sign goes before the $, not between it and the number -- `${n}` alone would render a negative
// variance as "$-3,000" instead of "-$3,000" (the only caller that ever passes a negative is the
// Actual-vs-Forecast variance column below).
function fmtMoney(n) {
  const v = Number(n || 0);
  return `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
// No trailing "h" (Jeremy, 2026-10-05) -- every column/card/row this feeds is already labeled Hours.
function fmtHours(n) {
  return Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function ProjectPicker({ projects, value, onChange }) {
  const [showArchived, setShowArchived] = useState(false);
  const [showPlaceholders, setShowPlaceholders] = useState(false);
  const visible = projects
    .filter((p) => !p.isAdministrative
      && (showPlaceholders || p.projectStatus !== 'Placeholder')
      && (showArchived || p.projectStatus !== 'Archived'))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div className="card-title">Project Budgets</div>
      <div style={{ display:'flex', gap:12, alignItems:'center' }}>
        <select className="form-select" style={{ maxWidth:420 }} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">— Select a project —</option>
          {visible.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}{p.account?.name ? ` · ${p.account.name}` : ''}{p.projectStatus === 'Archived' ? ' (archived)' : ''}{p.projectStatus === 'Placeholder' ? ' (placeholder)' : ''}
            </option>
          ))}
        </select>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-mid)' }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
          Show archived
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, color:'var(--text-mid)' }}>
          <input type="checkbox" checked={showPlaceholders} onChange={(e) => setShowPlaceholders(e.target.checked)} />
          Show placeholders
        </label>
        <Link to={projectPath('/registers', value, { tab: 'bau' })} style={{ marginLeft:'auto', fontSize:12 }}>
          Billed as Used Budget →
        </Link>
      </div>
    </div>
  );
}

// Actual then forecast as consecutive segments. The scale stretches past the budget when actual +
// forecast overruns it, with a tick where the budget ends, so an overrun is visible, not clipped.
// Color = the measure (hours light blue, revenue green); fill = actual (solid) vs. forecast (striped).
const HOURS_HUE = 'var(--accent-light-blue)'; // the lightest blue, to stay apart from the $ colors
const HOURS_LINE = 'var(--accent-blue)'; // the light blue is too faint for a line
const REVENUE_HUE = 'var(--green)';
const PICKUP_HUE = 'var(--accent-orange)';
const stripes =(hue) => `repeating-linear-gradient(45deg, ${hue} 0 3px, transparent 3px 6px)`;

function StackedBudgetBar({ planned, actual, forecast, hue }) {
  const total = actual + forecast;
  const scale = Math.max(planned, total) || 1;
  const w = (v) => `${Math.max(0, (v / scale) * 100)}%`;
  return (
    <div style={{ position:'relative', background:'var(--border)', borderRadius:4, height:10, overflow:'hidden', display:'flex' }}>
      <div title={`Actual ${Math.round((actual / planned) * 100)}%`} style={{ width:w(actual), background:hue }} />
      <div title={`Forecast ${Math.round((forecast / planned) * 100)}%`} style={{ width:w(forecast), backgroundImage:stripes(hue) }} />
      {total > planned && <div style={{ position:'absolute', left:w(planned), top:0, bottom:0, width:2, background:'var(--heading)' }} />}
    </div>
  );
}

// Planned/Actual/Remaining + % of budget used, one card for hours and one for $ (Jeremy,
// 2026-10-05) -- driven by budgetForecast()'s totals now that real actual-hours data exists,
// replacing the Planned-only cards this page carried while Financials was still unbuilt.
function SummaryCards({ forecast }) {
  if (!forecast) return null;
  const t = forecast.total;
  // The manual adjustment (Jeremy, 2026-10-05) only ever applies to hours -- there's no $ figure to
  // net it against yet, so the $ card's math is untouched.
  const adjustmentHours = forecast.adjustmentHours || 0;

  // Forecast = resource plan from this month forward; EAC = Actual + Forecast (+ adjustment).
  // Remaining = Planned minus EAC; EAC itself is no longer shown.
  const cards = [
    { title: 'Hours', planned: t.budgetedHours - (forecast.dilutionHours || 0), actual: t.actualHours, etc: t.forecastHours, eac: t.eacHours, adjustment: adjustmentHours, fmt: fmtHours, hue: HOURS_HUE },
    { title: 'Revenue', planned: t.budgetedAmount, actual: t.actualAmount, etc: t.forecastAmount, eac: t.eacAmount, adjustment: 0, fmt: fmtMoney, hue: REVENUE_HUE },
  ];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:16, marginBottom:16 }}>
      {cards.map(({ title, planned, actual, etc, eac, adjustment, fmt, hue }) => {
        // Planned - (Actual + Forecast); eac already carries any manual adjustment.
        const remaining = eac == null ? null : planned - eac;
        const pct = planned > 0 ? Math.round(((actual + adjustment) / planned) * 100) : null;
        return (
          <div key={title} className="card">
            <div className="card-title" style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline' }}>
              <span>{title}</span>
              {pct != null && <span style={{ fontSize:20, fontWeight:800, color:hue === HOURS_HUE ? HOURS_LINE : hue }} title="Actual to date as a share of planned">{pct}% Complete</span>}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:12, marginBottom:10 }}>
              {[['Planned', planned], ['Actual', actual], ['Forecast', etc], ['Remaining', remaining]].map(([label, value]) => (
                <div key={label}>
                  <div style={{ fontSize:11, color:'var(--text-mid)', marginBottom:4 }}>{label}</div>
                  <div style={{ fontSize:18, fontWeight:700, color: label === 'Remaining' && value < 0 ? 'var(--red)' : 'var(--heading)' }}>
                    {value == null ? '—' : fmt(value)}
                  </div>
                </div>
              ))}
            </div>
            {pct != null && (
              <>
                <StackedBudgetBar planned={planned} actual={actual + adjustment} forecast={etc || 0} hue={hue} />
                <div style={{ fontSize:11, color:'var(--text-mid)', marginTop:4 }}>
                  <span style={{ color:hue, fontWeight:600 }}>{pct}% actual</span>
                  {' · '}
                  <span style={{ color:hue, fontWeight:600 }}>{Math.round(((etc || 0) / planned) * 100)}% forecast (striped)</span>
                  {' · '}
                  <span style={{ color: actual + adjustment + (etc || 0) > planned * 1.1 ? 'var(--red)' : undefined, fontWeight: actual + adjustment + (etc || 0) > planned ? 600 : undefined }}>
                    {Math.round(((actual + adjustment + (etc || 0)) / planned) * 100)}% of budget at completion
                  </span>
                  {adjustment > 0 ? ` (incl. a ${fmtHours(adjustment)} adjustment)` : ''}                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Cumulative hours, recognized $ and billed $ by month on a calendar axis (year and quarter bands),
// with Go-Live and today marked. Reuses /budget-monthly-hours; `refreshKey` re-fetches after an adjustment.
function BudgetTimeline({ projectId, refreshKey }) {
  const [data, setData] = useState(null);
  const [goLive, setGoLive] = useState(null);
  const [billing, setBilling] = useState([]);
  // Drawn at the card's real width so text renders at true size rather than scaled up with the card.
  const boxRef = useRef(null);
  const [boxW, setBoxW] = useState(720);
  const [grain, setGrain] = useRememberedPeriod('projectBudgets.grain', 'qtr'); // 'month' | 'qtr' | 'year'
  const [shown, setShown] = useRememberedPeriod('projectBudgets.series', ['rec', 'bill']); // any of 'hours' | 'rec' | 'bill'
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setBoxW(Math.max(480, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [data]);

  useEffect(() => {
    let live = true;
    apiFetch(`/projects/${projectId}/budget-monthly-hours`).then((d) => { if (live) setData(d); }).catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [projectId, refreshKey]);

  useEffect(() => {
    let live = true;
    apiFetch(`/milestones?projectId=${projectId}`)
      .then((ms) => {
        const m = (ms || []).find((x) => x.isGoLive && (x.actualDate || x.targetDate));
        // Billable = a contracted amount; placed by the same date the Milestone Tracker uses.
        const billing = (ms || [])
          .filter((x) => Number(x.amount) > 0 && periodDateOf(x))
          .map((x) => ({ title: x.title, amount: Number(x.amount), done: isDone(x), date: String(periodDateOf(x)).slice(0, 10) }));
        if (live) {
          setGoLive(m ? { title: m.title, date: String(m.actualDate || m.targetDate).slice(0, 10) } : null);
          setBilling(billing);
        }
      })
      .catch(() => { if (live) { setGoLive(null); setBilling([]); } });
    return () => { live = false; };
  }, [projectId]);

  if (!data || !data.rows || data.rows.length === 0) return null;

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const idxOf = (y, m) => y * 12 + (m - 1);
  const parts = (iso) => { const [y, m, d] = iso.split('-').map(Number); return { y, m, d }; };
  const glp = goLive ? parts(goLive.date) : null;
  const now = new Date();

  const byIdx = new Map(data.rows.map((r) => [idxOf(r.year, r.month), r]));
  // Axis also extends to the go-live month if it falls beyond the last month with hours.
  // Billable milestones bucketed by month; the axis stretches to cover them too.
  const billByIdx = new Map();
  for (const b of billing) {
    const p = parts(b.date);
    if (!p.y || p.y < 2000 || p.y > 2100) continue; // a mistyped year would stretch the axis to thousands of columns
    const k = idxOf(p.y, p.m);
    const cur = billByIdx.get(k) || { amount: 0, allDone: true, items: [] };
    cur.amount += b.amount; cur.allDone = cur.allDone && b.done; cur.items.push(b);
    billByIdx.set(k, cur);
  }
  const first = Math.min(...byIdx.keys(), ...billByIdx.keys());
  const last = Math.max(...byIdx.keys(), ...billByIdx.keys(), glp ? idxOf(glp.y, glp.m) : -Infinity);
  // Months are summed into the picked grain's periods ("months" below means periods). A period still
  // holding any forecast month counts as forecast, so the solid line ends at the last finished one.
  const span = grain === 'year' ? 12 : grain === 'qtr' ? 3 : 1;
  const startOf = (i) => i - (i % span); // month index -> its period's first month (12 divides evenly by 3)
  const months = [];
  for (let i = first; i <= last; i += 1) {
    const r = byIdx.get(i);
    const s = startOf(i);
    let p = months[months.length - 1];
    if (!p || p.i !== s) { p = { i: s, year: Math.floor(s / 12), month: (s % 12) + 1, amount: 0, hours: 0, pickupHours: 0, billAmt: 0, forecast: false }; months.push(p); }
    p.amount += r?.amount ?? 0;
    p.hours += r ? (r.psHours || 0) + (r.rndHours || 0) : 0;
    p.pickupHours += r?.pickupHours || 0;
    p.billAmt += billByIdx.get(i)?.amount || 0;
    p.forecast = p.forecast || (r ? !!r.isForecast : i >= idxOf(now.getFullYear(), now.getMonth() + 1));
  }
  const kOf = new Map(months.map((m, k) => [m.i, k]));
  const periodName = (m) => (grain === 'year' ? `${m.year}` : grain === 'qtr' ? `Q${Math.ceil(m.month / 3)} ${m.year}` : `${MONTHS[m.month - 1]} ${m.year}`);

  const hasBilling = billByIdx.size > 0;
  const showRec = data.rows.some((r) => r.amount != null);
  const sHours = shown.includes('hours'); const sRec = showRec && shown.includes('rec'); const sBill = hasBilling && shown.includes('bill');
  // Restyled after the Milestone Tracker burn-down (Judy, 2026-11-02): cumulative lines, solid through
  // the last actual month and dashed for forecast, dots on actuals, gridlines, and red bands where
  // recognized revenue ran ahead of billing. Each month's own hours are in the hover.
  const laneH = sBill ? 48 : 0;
  const W = boxW; const H = 240 + laneH;
  const pad = { top: 6, right: 48, bottom: 36, left: 48 }; // left = hours axis, right = $ axis
  const bandH = 14; const barTop = pad.top + bandH * 2 + 16 + laneH; const barBottom = H - pad.bottom;
  const laneY = pad.top + bandH * 2 + 28; // center between the two marker rows
  const billTotal = [...billByIdx.values()].reduce((s, b) => s + b.amount, 0);
  const billDone = [...billByIdx.values()].reduce((s, b) => s + b.items.filter((i) => i.done).reduce((t, i) => t + i.amount, 0), 0);
  const moneyShort = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `$${Math.round(n / 1e3)}k` : `$${Math.round(n)}`);
  const hoursShort = (n) => (n >= 1e3 ? `${(n / 1e3).toFixed(1)}k` : `${Math.round(n)}`);
  const colW = (W - pad.left - pad.right) / months.length;
  // Cumulative hours and revenue: recognized = each month's hours at the rate in effect that month
  // (priced by the API), billed = billable milestones.
  let hCum = 0; let recCum = 0; let billCum = 0;
  months.forEach((m) => {
    hCum += m.hours;
    recCum += m.amount;
    billCum += m.billAmt;
    m.hCum = hCum; m.rec = recCum; m.bill = billCum;
  });
  const plotH = barBottom - barTop - 12;
  const maxHoursCum = Math.max(hCum, 1);

  const maxMoney = Math.max(sRec ? recCum : 0, sBill ? billCum : 0, 1);
  const yMoney = (v) => barBottom - (v / maxMoney) * plotH;
  const yHours = (v) => barBottom - (v / maxHoursCum) * plotH;
  const firstFc = months.findIndex((m) => m.forecast);
  months.forEach((m, k) => { m.k = k; });
  const cx = (m) => pad.left + (m.k + 0.5) * colW;
  const pathOf = (y, list) => list.map((m, n) => `${n === 0 ? 'M' : 'L'}${cx(m).toFixed(1)},${y(m).toFixed(1)}`).join(' ');
  const actualMonths = firstFc === -1 ? months : months.slice(0, firstFc);
  const fcMonths = firstFc === -1 ? [] : months.slice(Math.max(0, firstFc - 1));
  const lastActual = actualMonths[actualMonths.length - 1];
  const underbilled = lastActual ? lastActual.rec - lastActual.bill : 0;
  // One series = solid actual line + dashed forecast line + a dot on each actual month.
  const series = (y, color, key) => (
    <g key={key}>
      {actualMonths.length > 0 && <path d={pathOf(y, actualMonths)} fill="none" stroke={color} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />}
      {fcMonths.length > 1 && <path d={pathOf(y, fcMonths)} fill="none" stroke={color} strokeWidth="2" strokeDasharray="4 3" strokeLinecap="round" />}
      {colW >= 6 && actualMonths.map((m) => <circle key={m.i} cx={cx(m)} cy={y(m)} r="3" fill={color} stroke="var(--surface)" strokeWidth="1.25" />)}
    </g>
  );
  // Contiguous runs of months where recognized is ahead of billed, labeled like the burn-down's gaps.
  const aheadRuns = [];
  if (sRec && sBill) months.forEach((m) => {
    if (m.rec - m.bill <= 0.5) return;
    const lastRun = aheadRuns[aheadRuns.length - 1];
    if (lastRun && lastRun.end === m.k - 1) lastRun.end = m.k; else aheadRuns.push({ start: m.k, end: m.k });
  });
  // x of a month index (plus a fraction of that month) within its period's column.
  const xOfIdx = (i, frac) => pad.left + ((kOf.get(startOf(i)) ?? 0) + ((i - startOf(i)) + frac) / span) * colW;
  const xOfDate = (y, m, d) => {
    const dim = new Date(y, m, 0).getDate();
    return xOfIdx(idxOf(y, m), (d - 1) / dim);
  };
  const group = (keyOf) => {
    const out = [];
    months.forEach((m, k) => {
      const key = keyOf(m);
      if (out.length && out[out.length - 1].key === key) out[out.length - 1].n += 1;
      else out.push({ key, n: 1, start: k, sample: m });
    });
    return out;
  };
  const years = group((m) => m.year);
  const quarters = group((m) => `${m.year}-Q${Math.ceil(m.month / 3)}`);
  const todayInRange = idxOf(now.getFullYear(), now.getMonth() + 1) >= first && idxOf(now.getFullYear(), now.getMonth() + 1) <= last;
  // Every month gets a label; the font shrinks (and the name drops to one letter) as columns narrow.
  const monthFont = Math.max(7, Math.min(11, colW * 0.45));
  const monthName = (m) => (grain === 'year' ? m.year : grain === 'qtr' ? `Q${Math.ceil(m.month / 3)}${colW >= 40 ? ` '${String(m.year).slice(2)}` : ''}` : colW >= 22 ? MONTHS[m.month - 1] : MONTHS[m.month - 1][0]);
  const goLiveX = glp ? xOfDate(glp.y, glp.m, glp.d) : null;
  const goLiveLabel = glp ? `Go-Live ${MONTHS[glp.m - 1]} ${glp.d}, ${glp.y}` : '';
  const swatch = (color, dashed) => <span style={{ display:'inline-block', width:18, height:0, borderTop:`2px ${dashed ? 'dashed' : 'solid'} ${color}`, verticalAlign:'middle', marginRight:5 }} />;

  return (
    <div className="card" style={{ marginBottom:16 }} ref={boxRef}>
      <div className="mt-toolbar" style={{ justifyContent:'space-between', marginBottom:6 }}>
        <span className="card-title" style={{ marginBottom:0 }}>Hours by Period</span>
        {/* Pick one or more lines; at least one stays on. */}
        <div className="mt-seg" style={{ marginLeft:'auto' }} title="Lines shown">
          {[['hours', 'Hours'], ['rec', 'Recognized $'], ['bill', 'Billed $']].map(([k, l]) => (
            <button key={k} type="button" aria-pressed={shown.includes(k)}
              onClick={() => setShown((s) => (!s.includes(k) ? [...s, k] : s.length > 1 ? s.filter((x) => x !== k) : s))}>{l}</button>
          ))}
        </div>
        <div className="mt-seg">
          {[['month', 'Month'], ['qtr', 'Quarter'], ['year', 'Year']].map(([k, l]) => <button key={k} type="button" aria-pressed={grain === k} onClick={() => setGrain(k)}>{l}</button>)}
        </div>
      </div>
      <div style={{ display:'flex', gap:16, fontSize:11, color:'var(--text-mid)', marginBottom:4, flexWrap:'wrap', alignItems:'center' }}>
        {sHours && <span>{swatch(HOURS_LINE)}Hours (cumulative)</span>}
        {sRec && <span>{swatch(REVENUE_HUE)}Recognized $</span>}
        {sBill && <span>{swatch('var(--accent-violet)')}Billed $</span>}
        <span>{swatch('var(--text-mid)', true)}Dashed = forecast</span>
        {data.pickup && data.pickup.hours > 0 && <span><span style={{ color:PICKUP_HUE, fontWeight:800, marginRight:5 }}>$</span>Post-go-live pickup ({fmtHours(data.pickup.hours)})</span>}
        {sRec && sBill && <span style={{ color:'var(--danger-text)', fontWeight:600 }}><span style={{ display:'inline-block', width:10, height:10, background:'var(--row-overdue)', verticalAlign:'middle', marginRight:5 }} />Recognized ahead of billed</span>}
        <span><span style={{ display:'inline-block', width:2, height:10, background:'var(--accent-orange)', marginRight:5 }} />Today</span>
        {goLive && <span><span style={{ display:'inline-block', width:7, height:7, background:'var(--green)', transform:'rotate(45deg)', marginRight:6 }} />Go-Live</span>}
        {sBill && (
          <span>
            <span style={{ display:'inline-block', width:6, height:6, background:'var(--accent-violet)', transform:'rotate(45deg)', marginRight:6 }} />
            Billable milestones ({fmtMoney(billDone)} billed of {fmtMoney(billTotal)}; hollow = not yet)
          </span>
        )}
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:'auto', display:'block' }} role="img" aria-label="Cumulative hours and revenue by period, actual and forecast, with go-live marked">
        {years.map((g, k) => (
          <g key={`y-${g.key}`}>
            <rect x={pad.left + g.start * colW + 0.5} y={pad.top} width={g.n * colW - 1} height={bandH} fill="var(--light-bg)" />
            <text x={pad.left + (g.start + g.n / 2) * colW} y={pad.top + 11} textAnchor="middle" fontSize="11" fontWeight="700" fill="var(--heading)">{g.key}</text>
          </g>
        ))}
        {grain === 'month' && quarters.map((g) => (
          <g key={`q-${g.key}`}>
            <rect x={pad.left + g.start * colW + 0.5} y={pad.top + bandH + 1} width={g.n * colW - 1} height={bandH} fill="var(--light-bg)" opacity="0.6" />
            <text x={pad.left + (g.start + g.n / 2) * colW} y={pad.top + bandH + 11} textAnchor="middle" fontSize="10" fill="var(--text-mid)">{g.n >= 2 ? `Q${g.key.split('Q')[1]}` : ''}</text>
          </g>
        ))}
        {aheadRuns.map((r) => (
          <g key={`ub-${r.start}`}>
            <rect x={pad.left + r.start * colW} y={barTop - 8} width={(r.end - r.start + 1) * colW} height={barBottom - barTop + 8} fill="var(--row-overdue)" opacity="0.75" />
            {(r.end - r.start + 1) * colW >= 80 && (
              <text x={pad.left + ((r.start + r.end + 1) / 2) * colW} y={barTop + 3} textAnchor="middle" fontSize="11" fontWeight="600" fill="var(--danger-text)">rev ahead of billing</text>
            )}
          </g>
        ))}
        {/* Gridlines with the hours axis on the left and the $ axis on the right. */}
        {[0, 0.5, 1].map((f) => (
          <g key={`grid-${f}`}>
            <line x1={pad.left} x2={W - pad.right} y1={yHours(maxHoursCum * f)} y2={yHours(maxHoursCum * f)} stroke="var(--border)" />
            {sHours && <text x={pad.left - 6} y={yHours(maxHoursCum * f) + 3} textAnchor="end" fontSize="11" fill={HOURS_LINE}>{hoursShort(maxHoursCum * f)}h</text>}
            {(sRec || sBill) && <text x={W - pad.right + 6} y={yMoney(maxMoney * f) + 4} fontSize="11" fill="var(--text-mid)">{moneyShort(maxMoney * f)}</text>}
          </g>
        ))}
        {months.map((m) => <text key={`ml-${m.i}`} x={cx(m)} y={barBottom + 15} textAnchor="middle" fontSize={monthFont} fill="var(--text-mid)">{monthName(m)}</text>)}
        {sBill && (() => {
          // Neighbors whose $ labels would overprint alternate rows: one up (label above), one down
          // (label below). A lone marker stays on the upper row.
          let prevX = -Infinity; let prevRow = 1;
          return [...billByIdx.entries()].sort((a, b) => a[0] - b[0]).map(([k, b]) => {
            const bx = xOfIdx(k, 0.5);
            const row = bx - prevX < 44 ? 1 - prevRow : 0;
            prevX = bx; prevRow = row;
            const dy = laneY + (row ? 8 : -8);
            return (
              <g key={`bill-${k}`}>
                <polygon
                  points={`${bx},${dy - 4} ${bx + 4},${dy} ${bx},${dy + 4} ${bx - 4},${dy}`}
                  fill={b.allDone ? 'var(--accent-violet)' : 'var(--surface)'} stroke="var(--accent-violet)" strokeWidth="1.25"
                >
                  <title>{[`${MONTHS[k % 12]} ${Math.floor(k / 12)} — ${fmtMoney(b.amount)}`, ...b.items.map((i) => `  • ${i.title}: ${fmtMoney(i.amount)}${i.done ? ' (done)' : ''}`)].join('\n')}</title>
                </polygon>
                <text x={bx} y={row ? dy + 15 : dy - 8} textAnchor="middle" fontSize="10" fontWeight="600" fill="var(--accent-violet)">{moneyShort(b.amount)}</text>
              </g>
            );
          });
        })()}
        {sHours && series((m) => yHours(m.hCum), HOURS_LINE, 'hours')}
        {sBill && series((m) => yMoney(m.bill), 'var(--accent-violet)', 'bill')}
        {sRec && series((m) => yMoney(m.rec), REVENUE_HUE, 'rec')}
        {/* To-date values just above each line's dot at the last finished month; nudged apart when
            two dots sit close together so the labels don't overprint. */}
        {lastActual && (() => {
          const labels = [
            ...(sHours ? [{ y: yHours(lastActual.hCum), text: `${fmtHours(lastActual.hCum)} to date`, color: HOURS_LINE }] : []),
            ...(sRec ? [{ y: yMoney(lastActual.rec), text: `${moneyShort(lastActual.rec)} recognized`, color: REVENUE_HUE }] : []),
            ...(sBill ? [{ y: yMoney(lastActual.bill), text: `${moneyShort(lastActual.bill)} billed`, color: 'var(--accent-violet)' }] : []),
          ].sort((a, b) => b.y - a.y);
          let floor = Infinity;
          for (const l of labels) { l.ty = Math.min(l.y - 8, floor - 13); floor = l.ty; }
          return (
            <g fontSize="11" fontWeight="600" textAnchor="middle">
              {labels.map((l) => <text key={l.text} x={cx(lastActual)} y={l.ty} fill={l.color} stroke="var(--surface)" strokeWidth="2.5" paintOrder="stroke">{l.text}</text>)}
            </g>
          );
        })()}
        {months.map((m) => (
          <rect key={`hv-${m.i}`} x={pad.left + m.k * colW} y={barTop} width={colW} height={barBottom - barTop} fill="transparent">
            <title>{`${periodName(m)}${m.forecast ? ' (forecast)' : ' (actual)'}\nHours this period:${fmtHours(m.hours)} · cumulative ${fmtHours(m.hCum)}\nCumulative recognized: ${showRec ? fmtMoney(m.rec) : 'n/a (no rate)'}\nCumulative billed: ${fmtMoney(m.bill)}${showRec && m.rec - m.bill > 0.5 ? `\n⚠ Recognized ahead of billing by ${fmtMoney(m.rec - m.bill)}` : ''}`}</title>
          </rect>
        ))}
        {/* Pickup marker rides the Billed line (falls back to Recognized when nothing is billable); drawn
            last so it's hoverable. */}
        {months.filter((m) => m.pickupHours > 0).map((m) => (
          <text key={`pk-${m.i}`} x={cx(m)} y={(sBill ? yMoney(m.bill) : sRec ? yMoney(m.rec) : yHours(m.hCum)) - 5} textAnchor="middle" fontSize="12" fontWeight="800" fill={PICKUP_HUE} stroke="var(--surface)" strokeWidth="2.5" paintOrder="stroke">
            $
            <title>{`${periodName(m)}: ${fmtHours(m.pickupHours)} post-go-live pickup`}</title>
          </text>
        ))}
        {todayInRange && (
          <line x1={xOfDate(now.getFullYear(), now.getMonth() + 1, now.getDate())} x2={xOfDate(now.getFullYear(), now.getMonth() + 1, now.getDate())} y1={barTop - 8} y2={barBottom} stroke="var(--accent-orange)" strokeWidth="2" />
        )}
        {goLiveX != null && (
          <g>
            <line x1={goLiveX} x2={goLiveX} y1={barTop - 8} y2={barBottom} stroke="var(--green)" strokeWidth="1" strokeDasharray="3 3" opacity="0.6" />
            <polygon points={`${goLiveX},${barBottom - 6} ${goLiveX + 6},${barBottom} ${goLiveX},${barBottom + 6} ${goLiveX - 6},${barBottom}`} fill="var(--green)" stroke="var(--surface)" strokeWidth="1.25">
              <title>{goLiveLabel}</title>
            </polygon>
            <text x={goLiveX} y={barBottom + 30} textAnchor={goLiveX < 70 ? 'start' : goLiveX > W - 70 ? 'end' : 'middle'} fontSize="11" fontWeight="600" fill="var(--green)">{goLiveLabel}</text>
          </g>
        )}
      </svg>
      {showRec && lastActual && (
        <div style={{ fontSize:12, color:'var(--text-mid)', marginTop:4 }}>
          Through {periodName(lastActual)}: {fmtMoney(lastActual.rec)} recognized vs. {fmtMoney(lastActual.bill)} billed
          {' — '}
          <span style={{ fontWeight:600, color: underbilled > 0 ? 'var(--red)' : 'var(--text-dark)' }}>{underbilled > 0 ? '⚠ ' : ''}
            {underbilled >= 0 ? `${fmtMoney(underbilled)} recognized ahead of billing` : `${fmtMoney(-underbilled)} billed ahead of recognition`}
          </span>
        </div>
      )}
      {!showRec && <div style={{ fontSize:11, color:'var(--text-mid)', marginTop:4 }}>No billing rate set — the recognized-revenue line is unavailable.</div>}
      {!goLive && <div style={{ fontSize:11, color:'var(--text-mid)', marginTop:4 }}>No Go-Live milestone is set on this project's schedule.</div>}
    </div>
  );
}

function varianceClass(hours) {
  if (hours < -0.5) return 'var(--red)'; // tracking over budget
  if (hours > 0.5) return 'var(--green)'; // tracking under budget
  return 'var(--text-mid)';
}

// Hrs/$ as two columns, not one "80h (12,000)" string (Jeremy, 2026-10-05) -- each STAT (Budgeted,
// Actual, ...) renders as this pair of cells under a spanning header.
function StatCells({ hours, amount, variance }) {
  const color = variance ? varianceClass(hours) : undefined;
  const sign = '';
  return (
    <>
      <td style={{ padding:'8px 10px', color, fontWeight: variance ? 600 : undefined }}>{sign}{fmtHours(hours)}</td>
      <td style={{ padding:'8px 10px', color, fontWeight: variance ? 600 : undefined }}>{amount == null ? '—' : `${sign}${fmtMoney(amount)}`}</td>
    </>
  );
}

// EAC is still computed server-side but isn't shown. "Remaining" is the API's variance with the sign
// flipped (Budgeted minus EAC), so positive = under budget, matching the summary cards.
const STAT_DEFS = [
  { key: 'budgeted', label: 'Budgeted' },
  { key: 'actual', label: 'Actual' },
  { key: 'forecast', label: 'Forecast' },
  { key: 'variance', label: 'Remaining' },
];

// Manual hours deduction with a note (Jeremy, 2026-10-05) -- a stopgap for R&D-type work that eats
// into the budget without a real structure yet to track it on its own terms. Deliberately not the
// register-grid convention (dirty-tracking, Save N changes): this is two fields and an add/delete
// list, and that machinery would be more code than the feature.
function BudgetAdjustmentsCard({ projectId, adjustments, onChange }) {
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [confirmId, setConfirmId] = useState('');
  const [kind, setKind] = useState('Adjustment');
  const [effectiveDate, setEffectiveDate] = useState('');

  async function add() {
    const n = Number(hours);
    if (!(n > 0)) { setError('Enter hours greater than 0'); return; }
    if (!note.trim()) { setError('A note is required'); return; }
    if (kind === 'Dilution' && !effectiveDate) { setError('A dilution / reversal needs an effective date'); return; }
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${projectId}/budget-adjustments`, {
        method: 'POST',
        body: JSON.stringify({ hours: n, note: note.trim(), kind, ...(effectiveDate ? { effectiveDate } : {}) }),
      });
      setHours('');
      setNote('');
      setEffectiveDate('');
      await onChange();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id) {
    setError('');
    try {
      await apiFetch(`/budget-adjustments/${id}`, { method: 'DELETE' });
      setConfirmId('');
      await onChange();
    } catch (err) {
      setError(err.message);
    }
  }

  const total = adjustments.filter((a) => a.kind !== 'Dilution').reduce((s, a) => s + Number(a.hours), 0);

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div className="card-title">Budget Adjustments</div>
      <div style={{ fontSize:12, color:'var(--text-mid)', marginBottom:10 }}>
        A manual reduction against the hours available above — e.g. R&D work that isn't tracked on its own yet. A Dilution / reversal instead removes hours from the available budget from its effective date forward (it shows as a negative Dilution / Reversal column in the monthly breakdown). Each needs a note.
      </div>
      {error && <span style={{ fontSize:12, color:'var(--red)', display:'block', marginBottom:8 }}>⚠ {error}</span>}

      {adjustments.length > 0 && (
        <table style={{ borderCollapse:'collapse', width:'100%', fontSize:13, marginBottom:10 }}>
          <thead>
            <tr style={{ background:'var(--light-bg)', textAlign:'left' }}>
              <th style={{ padding:'6px 10px', width:90 }}>Hours</th>
              <th style={{ padding:'6px 10px' }}>Type</th>
              <th style={{ padding:'6px 10px' }}>Effective</th>
              <th style={{ padding:'6px 10px' }}>Note</th>
              <th style={{ width:36 }} />
            </tr>
          </thead>
          <tbody>
            {adjustments.map((a) => (
              <tr key={a.id} style={{ borderTop:'1px solid var(--border)' }}>
                <td style={{ padding:'6px 10px' }}>{a.kind === 'Dilution' ? `−${fmtHours(a.hours)}` : fmtHours(a.hours)}</td>
                <td style={{ padding:'6px 10px' }}>{a.kind === 'Dilution' ? 'Dilution / reversal' : 'Adjustment'}</td>
                <td style={{ padding:'6px 10px' }}>{a.effectiveDate ? String(a.effectiveDate).slice(0, 10) : '—'}</td>
                <td style={{ padding:'6px 10px' }}>{a.note}</td>
                <td className="cell-check">
                  {confirmId === a.id ? (
                    <button className="icon-btn" onClick={() => remove(a.id)} title="Confirm delete — can't be undone">✓?</button>
                  ) : (
                    <button className="icon-btn" onClick={() => setConfirmId(a.id)} title="Delete this adjustment">✕</button>
                  )}
                </td>
              </tr>
            ))}
            <tr style={{ borderTop:'2px solid var(--border)', fontWeight:700 }}>
              <td style={{ padding:'6px 10px' }}>{fmtHours(total)}</td>
              <td style={{ padding:'6px 10px' }} colSpan={4}>Total adjustments (dilutions excluded)</td>
            </tr>
          </tbody>
        </table>
      )}

      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <select className="form-select" style={{ width:150 }} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="Adjustment">Adjustment</option>
          <option value="Dilution">Dilution / reversal</option>
        </select>
        <input
          type="number" min="0" step="0.5" className="form-input" style={{ width:90 }}
          placeholder="Hours" value={hours} onChange={(e) => setHours(e.target.value)}
        />
        {kind === 'Dilution' && (
          <input type="date" className="form-input" style={{ width:150 }} title="Effective date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
        )}
        <input
          type="text" className="form-input" style={{ flex:1, minWidth:200 }}
          placeholder="Note — what is this?" value={note} onChange={(e) => setNote(e.target.value)}
        />
        <button className="btn btn-outline" style={{ padding:'4px 10px', fontSize:12 }} onClick={add} disabled={saving}>
          {saving ? 'Adding…' : '+ Add'}
        </button>
      </div>
    </div>
  );
}

// Plan vs. Actual vs. Forecast, by team (Jeremy, 2026-10-05): actual/forecast hours AND dollars back
// on this page (dropped 2026-09-28 pending Financials), now that GET /budget-forecast has a real
// source for both.
function ForecastSection({ forecast, projectName, onOpenMonthly }) {
  const [teamFilter, setTeamFilter] = useState([]);
  const [expanded, setExpanded] = useState(() => new Set()); // 'actual' / 'forecast' groups showing per-year columns

  if (!forecast) return null;

  const filterOptions = [
    ...forecast.byTeam.map((t) => ({ value: t.teamId, label: t.teamName })),
    ...(forecast.unassigned ? [{ value: '__unassigned', label: forecast.unassigned.teamName }] : []),
  ];
  const hasProjectLevel = forecast.projectLevel?.plannedHours > 0 || forecast.projectLevel?.plannedAmount > 0;
  if (hasProjectLevel) filterOptions.push({ value: '__projectLevel', label: 'Project level' });
  const filtered = teamFilter.length > 0;
  const rows = filtered ? forecast.byTeam.filter((t) => teamFilter.includes(t.teamId)) : forecast.byTeam;
  const showUnassignedRow = forecast.unassigned && (!filtered || teamFilter.includes('__unassigned'));
  const showTotalRow = true;
  // Project-level budget lines carry no team, so they were only visible inside the Total.
  const showProjectLevelRow = hasProjectLevel && (!filtered || teamFilter.includes('__projectLevel'));
  // Budget only; Remaining is budget minus nothing spent (varianceX is EAC minus budget, see STAT_DEFS).
  const projectLevelSrc = hasProjectLevel ? {
    budgetedHours: forecast.projectLevel.plannedHours, budgetedAmount: forecast.projectLevel.plannedAmount,
    varianceHours: -forecast.projectLevel.plannedHours, varianceAmount: -forecast.projectLevel.plannedAmount,
  } : null;
  // Filtered total is just the visible rows summed; unfiltered keeps the server total, which also
  // carries the project-level budget and any manual adjustment.
  const sumField = (list, get) => {
    const vals = list.map(get).filter((v) => v != null);
    return vals.length ? vals.reduce((s, v) => s + Number(v), 0) : null;
  };
  const totalSrc = (() => {
    if (!filtered) return forecast.total;
    const list = [...rows, ...(showUnassignedRow ? [forecast.unassigned] : []), ...(showProjectLevelRow ? [projectLevelSrc] : [])];
    const fields = ['budgeted', 'actual', 'forecast', 'variance'].flatMap((k) => [`${k}Hours`, `${k}Amount`]);
    const out = { byYear: {} };
    for (const f of fields) out[f] = sumField(list, (r) => r[f]) ?? (f.endsWith('Hours') ? 0 : null);
    for (const y of forecast.years || []) {
      out.byYear[y] = {};
      for (const k of ['actual', 'forecast']) {
        out.byYear[y][`${k}Hours`] = sumField(list, (r) => r.byYear?.[y]?.[`${k}Hours`]) ?? 0;
        out.byYear[y][`${k}Amount`] = sumField(list, (r) => r.byYear?.[y]?.[`${k}Amount`]);
      }
    }
    return out;
  })();

  // Actual and Forecast show one total column; when the project has data in more than one year the
  // group header gets a toggle that adds a column per year that has data for that group.
  const years = forecast.years || [];
  const yearsWithData = (key) => years.filter((y) => {
    const c = forecast.total.byYear?.[y];
    return c && (c[`${key}Hours`] > 0 || c[`${key}Amount`] > 0);
  });
  const groups = STAT_DEFS.map(({ key, label }) => {
    const all = {
      label: '', variance: key === 'variance',
      get: (src) => (key === 'variance'
        ? { hours: -src.varianceHours, amount: src.varianceAmount == null ? null : -src.varianceAmount }
        : { hours: src[`${key}Hours`], amount: src[`${key}Amount`] }),
    };
    const dataYears = key === 'actual' || key === 'forecast' ? yearsWithData(key) : [];
    const expandable = dataYears.length > 1;
    const isOpen = expandable && expanded.has(key);
    const perYear = isOpen
      ? dataYears.map((y) => ({
        label: String(y),
        get: (src) => ({ hours: src.byYear?.[y]?.[`${key}Hours`] ?? 0, amount: src.byYear?.[y]?.[`${key}Amount`] ?? null }),
      }))
      : [];
    if (isOpen) all.label = 'All years';
    return { key, label, expandable, isOpen, subs: [all, ...perYear] };
  });
  const anyOpen = groups.some((g) => g.isOpen);
  const toggleGroup = (key) => setExpanded((prev) => {
    const next = new Set(prev);
    if (!next.delete(key)) next.add(key);
    return next;
  });

  function exportXlsx() {
    const toRow = (name, src, skip = []) => {
      const row = { Team: name };
      for (const g of groups) {
        for (const sub of g.subs) {
          const blank = skip.includes(g.key);
          const head = `${g.label}${sub.label ? ` ${sub.label}` : ''}`;
          const v = sub.get(src);
          row[`${head} Hrs`] = blank ? '' : v.hours;
          row[`${head} Revenue`] = blank ? '' : v.amount;
        }
      }
      return row;
    };
    const sheetRows = rows.map((t) => toRow(t.teamName, t));
    if (showProjectLevelRow) {
      sheetRows.push(toRow('Project level', projectLevelSrc, ['actual', 'forecast', 'variance']));
    }
    if (showUnassignedRow) sheetRows.push(toRow(forecast.unassigned.teamName, forecast.unassigned, ['budgeted', 'variance']));
    sheetRows.push(toRow(filtered ? 'Total (selected teams)' : 'Total', totalSrc));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheetRows), 'Plan vs Actual vs Forecast');
    const safeName = (projectName || '').replace(/[\\/:*?"<>|]/g, '-').trim();
    XLSX.writeFile(wb, `${safeName ? `${safeName} - ` : ''}Plan vs Actual vs Forecast.xlsx`);
  }

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10, marginBottom:8 }}>
        <div className="card-title" style={{ marginBottom:0 }}>Plan vs. Actual vs. Forecast</div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <button className="btn btn-outline" style={{ padding:'4px 10px', fontSize:12 }} onClick={exportXlsx}>
            Export to Excel
          </button>
          <button className="btn btn-outline" style={{ padding:'4px 10px', fontSize:12 }} onClick={onOpenMonthly}>
            Monthly breakdown →
          </button>
          <MultiSelect
            label="Team" options={filterOptions} selected={teamFilter} onChange={setTeamFilter} allLabel="All teams"
          />
        </div>
      </div>

      <div style={{ fontSize:12, color:'var(--text-mid)', marginBottom:12 }}>
        Actuals (billable timesheet hours) through {forecast.asOf.actualThrough} · Forecast (resource plan) from {forecast.asOf.forecastFrom} forward
        {forecast.rate == null && ' · No billing rate set — $ figures unavailable'}
      </div>

      {rows.length === 0 && !showTotalRow ? (
        <div style={{ fontSize:13, color:'var(--text-mid)' }}>No hours for this team yet.</div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ borderCollapse:'collapse', width:'100%', fontSize:13 }}>
            <thead>
              <tr style={{ background:'var(--light-bg)', textAlign:'left' }}>
                <th rowSpan={anyOpen ? 3 : 2} style={{ padding:'8px 10px', verticalAlign:'bottom' }}>Team</th>
                {groups.map((g) => (
                  <th key={g.key} colSpan={g.subs.length * 2} style={{ padding:'8px 10px', borderLeft:'1px solid var(--border)' }}>
                    {g.label}
                    {g.expandable && (
                      <button
                        className="icon-btn" style={{ marginLeft:6 }} onClick={() => toggleGroup(g.key)}
                        title={g.isOpen ? 'Hide years' : 'Show by year'}
                      >
                        {g.isOpen ? '−' : '+'}
                      </button>
                    )}
                  </th>
                ))}
              </tr>
              {anyOpen && (
                <tr style={{ background:'var(--light-bg)', textAlign:'left', fontSize:11, color:'var(--text-mid)' }}>
                  {groups.map((g) => g.subs.map((sub, i) => (
                    <th key={`${g.key}-${i}`} colSpan={2} style={{ padding:'0 10px 2px', borderLeft:'1px solid var(--border)', fontWeight:600 }}>{sub.label}</th>
                  )))}
                </tr>
              )}
              <tr style={{ background:'var(--light-bg)', textAlign:'left', fontSize:11, color:'var(--text-mid)' }}>
                {groups.map((g) => g.subs.map((sub, i) => (
                  <React.Fragment key={`${g.key}-${i}`}>
                    <th style={{ padding:'0 10px 6px', borderLeft:'1px solid var(--border)', fontWeight:400 }}>Hrs</th>
                    <th style={{ padding:'0 10px 6px', fontWeight:400 }}>Revenue</th>
                  </React.Fragment>
                )))}
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.teamId || 'unassigned'} style={{ borderTop:'1px solid var(--border)' }}>
                  <td style={{ padding:'8px 10px' }}>{t.teamName}</td>
                  {groups.map((g) => g.subs.map((sub, i) => (
                    <StatCells key={`${g.key}-${i}`} {...sub.get(t)} variance={sub.variance} />
                  )))}
                </tr>
              ))}
              {showProjectLevelRow && (
                <tr style={{ borderTop:'1px solid var(--border)' }}>
                  <td style={{ padding:'8px 10px' }}>Project level</td>
                  {groups.map((g) => (g.key === 'budgeted'
                    ? <StatCells key={g.key} hours={forecast.projectLevel.plannedHours} amount={forecast.projectLevel.plannedAmount} />
                    : <td key={g.key} colSpan={g.subs.length * 2} style={{ padding:'8px 10px' }}>—</td>))}
                </tr>
              )}
              {showUnassignedRow && (
                <tr style={{ borderTop:'1px solid var(--border)' }}>
                  <td style={{ padding:'8px 10px', color:'var(--text-mid)' }}>{forecast.unassigned.teamName}</td>
                  {groups.map((g) => (g.key === 'budgeted' || g.key === 'variance'
                    ? <td key={g.key} colSpan={2} style={{ padding:'8px 10px' }}>—</td>
                    : g.subs.map((sub, i) => <StatCells key={`${g.key}-${i}`} {...sub.get(forecast.unassigned)} />)))}
                </tr>
              )}
              {showTotalRow && (
                <tr style={{ borderTop:'2px solid var(--border)', fontWeight:700 }}>
                  <td style={{ padding:'8px 10px' }}>
                    {filtered ? 'Total (selected teams)' : 'Total'}
                    {!filtered && forecast.projectLevel.plannedHours > 0 ? ' (incl. project-level budget)' : ''}
                    {!filtered && forecast.adjustmentHours > 0 ? ` (Remaining incl. a${fmtHours(forecast.adjustmentHours)}-hour adjustment)` : ''}
                  </td>
                  {groups.map((g) => g.subs.map((sub, i) => (
                    <StatCells key={`${g.key}-${i}`} {...sub.get(totalSrc)} variance={sub.variance} />
                  )))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Post-go-live pickup: the budget left after ACT + FCST, parked in one month instead of piled into
// the last forecast month. Only the month is stored; hours/revenue come from the API and move with
// every actuals/forecast change.
function PickupCard({ projectId, forecast, onChange }) {
  const [goLive, setGoLive] = useState(null);
  const [editing, setEditing] = useState(false);
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  useEffect(() => {
    let live = true;
    apiFetch(`/milestones?projectId=${projectId}`)
      .then((ms) => {
        const m = (ms || []).find((x) => x.isGoLive && (x.actualDate || x.targetDate));
        if (live) setGoLive(m ? String(m.actualDate || m.targetDate).slice(0, 10) : null);
      })
      .catch(() => { if (live) setGoLive(null); });
    return () => { live = false; };
  }, [projectId]);

  if (!forecast) return null;
  const pickup = forecast.pickup;
  const unforecast = Math.max(0, (forecast.total?.budgetedHours || 0) - (forecast.total?.eacHours || 0));

  function startEdit() {
    // Default: two months after go-live, else two months from now.
    const base = goLive ? new Date(`${goLive}T00:00:00`) : new Date();
    const d = new Date(base.getFullYear(), base.getMonth() + 2, 1);
    setYear(String(pickup?.year ?? d.getFullYear()));
    setMonth(String(pickup?.month ?? d.getMonth() + 1));
    setError('');
    setEditing(true);
  }

  async function save() {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${projectId}/pickup`, {
        method: 'PUT',
        body: JSON.stringify({ pickupYear: Number(year), pickupMonth: Number(month) }),
      });
      setEditing(false);
      await onChange();
    } catch (err) {
      setError('Could not save — ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/projects/${projectId}/pickup`, { method: 'DELETE' });
      await onChange();
    } catch (err) {
      setError('Could not remove — ' + err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="card" style={{ marginBottom:16 }}>
      <div className="card-title">Post-go-live pickup</div>
      <ErrorBanner error={error} />
      {pickup ? (
        <div style={{ fontSize:13 }}>
          <strong>{fmtHours(pickup.hours)}</strong>
          {pickup.amount != null && <> ({fmtMoney(pickup.amount)})</>} in <strong>{MONTH_NAMES[pickup.month - 1]} {pickup.year}</strong>,
          priced at that month's rate. It's whatever budget is left after actuals and forecast, so it changes as they do.
          {pickup.overrunHours > 0 && (
            <div style={{ color:'var(--red)', marginTop:4 }}>Actuals + forecast already exceed the budget by {fmtHours(pickup.overrunHours)}; nothing is left to pick up.</div>
          )}
          <div style={{ marginTop:8, display:'flex', gap:8 }}>
            <button className="btn btn-outline" style={{ padding:'4px 10px', fontSize:12 }} onClick={startEdit} disabled={saving}>Change month</button>
            <button className="btn btn-outline" style={{ padding:'4px 10px', fontSize:12 }} onClick={remove} disabled={saving}>Remove</button>
          </div>
        </div>
      ) : (
        <div style={{ fontSize:13 }}>
          {unforecast > 0 ? `${fmtHours(unforecast)} of budget isn't forecast.` : 'No budget is left after actuals and forecast, so a pickup would be 0 h for now.'}
          {goLive ? ` Go-live is ${goLive}.` : ''} If this project is going live, mark the remaining hours as post-go-live pickup in one month rather than forecasting them
          into the last month, so they don't distort utilization.
          <div style={{ marginTop:8 }}>
            <button className="btn btn-primary" style={{ padding:'4px 10px', fontSize:12 }} onClick={startEdit} disabled={saving}>Mark as going live…</button>
          </div>
        </div>
      )}
      {editing && (
        <div style={{ marginTop:10, display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', fontSize:13 }}>
          Pickup month
          <select className="form-select" style={{ width:90 }} value={month} onChange={(e) => setMonth(e.target.value)}>
            {MONTH_NAMES.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
          </select>
          <input className="form-input" type="number" style={{ width:90 }} value={year} onChange={(e) => setYear(e.target.value)} />
          <button className="btn btn-primary" style={{ padding:'4px 10px', fontSize:12 }} onClick={save} disabled={saving || !(Number(year) >= 2000)}>Save</button>
          <button className="btn btn-outline" style={{ padding:'4px 10px', fontSize:12 }} onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
        </div>
      )}
    </div>
  );
}

// Month-by-month hours burn-down, popped out rather than inlined on the page (Jeremy, 2026-10-05):
// secondary to the totals above, and a full table per month would dominate the page otherwise.
// Fetched lazily on open, not as part of ProjectBudgetDetail's reload() -- rarely opened, and its
// own project-wide TimeEntry scan is the heaviest query on this page.
function MonthlyBreakdownModal({ projectId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch(`/projects/${projectId}/budget-monthly-hours`)
      .then(setData)
      .catch((err) => setError('Could not load monthly hours — ' + err.message));
  }, [projectId]);

  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={onClose}
    >
      <div className="card" style={{ maxWidth:600, width:'90%', maxHeight:'80vh', overflowY:'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
          <div className="card-title" style={{ marginBottom:0 }}>Monthly Hours</div>
          <button className="icon-btn" onClick={onClose} title="Close">✕</button>
        </div>
        <ErrorBanner error={error} />
        {!data && !error ? (
          <div style={{ textAlign:'center', padding:24, color:'var(--text-mid)', fontSize:13 }}>Loading…</div>
        ) : data && (
          <>
            <div style={{ fontSize:12, color:'var(--text-mid)', marginBottom:10 }}>
              Budgeted: {fmtHours(data.budgetedHours)}{data.adjustmentHours > 0 ? ` (less a ${fmtHours(data.adjustmentHours)}-hour adjustment)` : ''}. A dilution / reversal lowers Remaining from its month forward. Past months are actual (billable timesheet hours plus R&D entries); the current month forward is forecast (resource plan plus R&D forecast).
            </div>
            {data.rows.length === 0 ? (
              <div style={{ fontSize:13, color:'var(--text-mid)' }}>No hours logged or forecast yet.</div>
            ) : (
              <table style={{ borderCollapse:'collapse', width:'100%', fontSize:13 }}>
                <thead>
                  <tr style={{ background:'var(--light-bg)', textAlign:'left' }}>
                    <th style={{ padding:'6px 10px' }}>Month</th>
                    <th style={{ padding:'6px 10px' }}>PS Hours</th>
                    <th style={{ padding:'6px 10px' }}>R&D Hours</th>
                    <th style={{ padding:'6px 10px' }}>Pickup</th>
                    <th style={{ padding:'6px 10px' }}>Dilution / Reversal</th>
                    <th style={{ padding:'6px 10px' }}>Cumulative</th>
                    <th style={{ padding:'6px 10px' }}>Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={`${r.year}-${r.month}`} style={{ borderTop:'1px solid var(--border)' }}>
                      <td style={{ padding:'6px 10px' }}>
                        {r.label}
                        {r.isForecast && <span className="badge badge-blue" style={{ marginLeft:6, fontSize:9 }}>FCST</span>}
                      </td>
                      <td style={{ padding:'6px 10px' }}>{fmtHours(r.psHours)}</td>
                      <td style={{ padding:'6px 10px' }}>{fmtHours(r.rndHours)}</td>
                      <td style={{ padding:'6px 10px' }}>{r.pickupHours > 0 ? fmtHours(r.pickupHours) : '—'}</td>
                      <td style={{ padding:'6px 10px', color: r.dilutionHours < 0 ? 'var(--red)' : undefined }}>{r.dilutionHours < 0 ? fmtHours(r.dilutionHours) : '—'}</td>
                      <td style={{ padding:'6px 10px' }}>{fmtHours(r.cumulativeHours)}</td>
                      <td style={{ padding:'6px 10px', color: r.remainingHours < 0 ? 'var(--red)' : undefined }}>{fmtHours(r.remainingHours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Current project billing rate ($/hr), with a control to set/change it. Setting a rate always adds
 * a new BillingRate row rather than editing one in place (server-enforced) -- the history is what
 * lets a later reviewer see what rate a plan's dollars were actually calculated against.
 */
function BillingRateControl({ rate, onSet }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function startEdit() {
    setValue(rate != null ? String(rate) : '');
    setEffectiveDate(new Date().toISOString().slice(0, 10));
    setError('');
    setEditing(true);
  }

  async function save() {
    const n = Number(value);
    if (value === '' || !(n > 0)) { setError('Enter a rate greater than 0'); return; }
    if (!effectiveDate) { setError('Pick an effective date'); return; }
    setSaving(true);
    setError('');
    try {
      await onSet(n, effectiveDate);
      setEditing(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, flexWrap:'wrap' }}>
      <span style={{ color:'var(--text-mid)' }}>Billing rate:</span>
      {editing ? (
        <>
          <input
            type="number" min="0" step="0.01" className="form-input" style={{ width:90 }}
            value={value} onChange={(e) => setValue(e.target.value)} autoFocus
          />
          <span style={{ color:'var(--text-mid)' }}>/hr, effective</span>
          <input
            type="date" className="form-input" style={{ width:140 }}
            value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)}
          />
          <button className="btn btn-primary" style={{ padding:'4px 10px', fontSize:12 }} onClick={save} disabled={saving}>
            {saving ? '…' : 'Save'}
          </button>
          <button className="btn btn-outline" style={{ padding:'4px 10px', fontSize:12 }} onClick={() => setEditing(false)}>
            Cancel
          </button>
        </>
      ) : (
        <>
          <strong>{rate != null ? `$${Number(rate).toFixed(2)}/hr` : 'not set'}</strong>
          <button className="btn btn-outline" style={{ padding:'4px 10px', fontSize:12 }} onClick={startEdit}>
            {rate != null ? 'Change' : 'Set rate'}
          </button>
        </>
      )}
      {error && <span style={{ fontSize:12, color:'var(--red)' }}>⚠ {error}</span>}
    </div>
  );
}

// Category/team sort read off the nested `category`/`team` objects the API includes -- a row's
// dropdown edit changes only its scalar id (see editCell), not that nested name, so a just-edited
// row can sort by its old name until Save reloads. Matches this grid's existing precedent of only
// resolving names on reload, not live.
const BUDGET_SORT_VALUE = {
  category: (r) => lower(r.category?.name),
  team: (r) => lower(r.team?.name),
  plannedHours: (r) => (r.plannedHours == null ? -Infinity : Number(r.plannedHours)),
  plannedAmount: (r) => (r.plannedAmount == null ? -Infinity : Number(r.plannedAmount)),
};

function BudgetLinesGrid({ lines, teams, categories, dirtyIds, sort, onSort, onEdit, onDelete, pinnedId }) {
  const [confirmId, setConfirmId] = useState('');
  const ranked = sortRows(lines, sort, (row, key) => BUDGET_SORT_VALUE[key](row));
  // The line just added always leads the grid regardless of sort (Jeremy, 2026-09-28), so it's
  // visible without hunting for wherever it landed -- cleared the moment the user picks a sort
  // column themselves (see toggleSort's wrapper in ProjectBudgetDetail).
  const sorted = pinnedId
    ? [...ranked.filter((r) => r.id === pinnedId), ...ranked.filter((r) => r.id !== pinnedId)]
    : ranked;

  return (
    <div className="sheet-wrap">
      <table className="sheet">
        <thead>
          <tr>
            <SortTh label="Category" sortKey="category" sort={sort} onSort={onSort} />
            <SortTh label="Team" sortKey="team" sort={sort} onSort={onSort} />
            <SortTh label="Planned Hours" sortKey="plannedHours" sort={sort} onSort={onSort} style={{ width:110 }} />
            <SortTh label="Planned $" sortKey="plannedAmount" sort={sort} onSort={onSort} style={{ width:110 }} />
            <th style={{ width:36 }} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((line, i) => (
            <tr key={line.id} className={rowClassName(i, dirtyIds.has(line.id))}>
              <td>
                <select className="cell-select" value={line.categoryId} onChange={(e) => onEdit(line.id, 'categoryId', e.target.value)}>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </td>
              <td>
                <select className="cell-select" value={line.teamId || ''} onChange={(e) => onEdit(line.id, 'teamId', e.target.value)}>
                  <option value="">— Project-level —</option>
                  {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </td>
              <td>
                <input
                  type="number" min="0" className="cell-input" style={{ textAlign:'right' }}
                  value={line.plannedHours ?? ''} onChange={(e) => onEdit(line.id, 'plannedHours', e.target.value)}
                />
              </td>
              <td>
                <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                  <span style={{ color:'var(--text-mid)' }}>$</span>
                  <input
                    type="number" min="0" step="0.01" className="cell-input" style={{ textAlign:'right' }}
                    value={line.plannedAmount ?? ''} onChange={(e) => onEdit(line.id, 'plannedAmount', e.target.value)}
                  />
                </div>
              </td>
              <td className="cell-check">
                {confirmId === line.id ? (
                  <button className="icon-btn" onClick={() => { setConfirmId(''); onDelete(line.id); }} title="Confirm delete — can't be undone">✓?</button>
                ) : (
                  <button className="icon-btn" onClick={() => setConfirmId(line.id)} title="Delete this line">✕</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// categoryId/teamId/clearTeam/plannedHours/plannedAmount only -- actualAmount is deliberately left
// out of every payload this grid builds, so editing here can never clobber whatever manually-entered
// actual is already on a line (Jeremy, 2026-09-28: Actual $ comes off this UI "for now").
function buildPayload(line) {
  return {
    categoryId: line.categoryId,
    teamId: line.teamId || undefined,
    clearTeam: !line.teamId,
    plannedHours: line.plannedHours === '' || line.plannedHours == null ? undefined : Number(line.plannedHours),
    plannedAmount: line.plannedAmount === '' || line.plannedAmount == null ? 0 : Number(line.plannedAmount),
  };
}

function ImportPreviewModal({ preview, existingCount, busy, onConfirm, onRemove, onClose }) {
  const totalHrs = preview.lines.reduce((s, l) => s + l.plannedHours, 0);
  const totalAmt = preview.lines.reduce((s, l) => s + l.plannedAmount, 0);
  return (
    <div
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}
      onClick={busy ? undefined : onClose}
    >
      <div className="card" style={{ maxWidth:760, width:'92%', maxHeight:'85vh', overflowY:'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="card-title">Import estimate{preview.county ? ` — ${preview.county}` : ''}</div>
        {existingCount > 0 && (
          <div className="proxy-banner" style={{ marginBottom:10 }}>
            ⚠ This project already has {existingCount} budget line(s). Importing adds these on top — nothing is replaced.
          </div>
        )}
        {preview.lines.length === 0 ? (
          <div style={{ fontSize:13, color:'var(--text-mid)' }}>No in-scope lines with hours were found.</div>
        ) : (
          <table style={{ borderCollapse:'collapse', width:'100%', fontSize:13 }}>
            <thead>
              <tr style={{ background:'var(--light-bg)', textAlign:'left' }}>
                <th style={{ padding:'6px 10px' }}>Category</th>
                <th style={{ padding:'6px 10px' }}>Team</th>
                <th style={{ padding:'6px 10px', textAlign:'right' }}>Hours</th>
                <th style={{ padding:'6px 10px', textAlign:'right' }}>$</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {preview.lines.map((l, i) => (
                <tr key={i} style={{ borderTop:'1px solid var(--border)' }}>
                  <td style={{ padding:'6px 10px' }}>{l.name.length > 60 ? `${l.name.slice(0, 60)}…` : l.name}</td>
                  <td style={{ padding:'6px 10px', color:'var(--text-mid)' }}>{l.teamName || '—'}</td>
                  <td style={{ padding:'6px 10px', textAlign:'right' }}>{fmtHours(l.plannedHours)}</td>
                  <td style={{ padding:'6px 10px', textAlign:'right' }}>{fmtMoney(l.plannedAmount)}</td>
                  <td style={{ padding:'6px 4px' }}>
                    <button className="icon-btn" title="Remove this line" onClick={() => onRemove(i)} disabled={busy}>✕</button>
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop:'2px solid var(--border)', fontWeight:700 }}>
                <td style={{ padding:'6px 10px' }} colSpan={2}>Total</td>
                <td style={{ padding:'6px 10px', textAlign:'right' }}>{fmtHours(totalHrs)}</td>
                <td style={{ padding:'6px 10px', textAlign:'right' }}>{fmtMoney(totalAmt)}</td>
                <td />
              </tr>
            </tbody>
          </table>
        )}
        {preview.skipped.length > 0 && (
          <div style={{ fontSize:12, color:'var(--text-mid)', marginTop:10 }}>
            Skipped: {preview.skipped.join('; ')}
          </div>
        )}
        <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={busy || preview.lines.length === 0}>
            {busy ? 'Importing…' : `Create ${preview.lines.length} line(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectBudgetDetail({ projectId, projectName }) {
  const [lines, setLines] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [adjustments, setAdjustments] = useState([]);
  const [showMonthly, setShowMonthly] = useState(false);
  const [teams, setTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rate, setRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [dirtyIds, setDirtyIds] = useState(() => new Set());
  const [busy, setBusy] = useState('');
  const [sort, toggleSort] = useGridSort('');
  const [pinnedId, setPinnedId] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const importInput = useRef(null);

  function handleSort(key) {
    setPinnedId(null);
    toggleSort(key);
  }

  useRegisterDirty(dirtyIds.size > 0);

  // Shared by reload() and BudgetAdjustmentsCard's add/delete -- an adjustment changes
  // forecast.adjustmentHours too, so both need to come back in sync.
  const reloadForecast = useCallback(() => {
    apiFetch(`/projects/${projectId}/budget-forecast`).then(setForecast).catch(() => setForecast(null));
    apiFetch(`/projects/${projectId}/budget-adjustments`).then(setAdjustments).catch(() => setAdjustments([]));
  }, [projectId]);

  const reload = useCallback(() => {
    setError('');
    // budget-forecast/budget-adjustments are their own fetch, not entries in the Promise.all below:
    // that's a heavier, newer aggregation (TimeEntry + ResourceMonthlyAllocation +
    // TeamMonthlyAllocation), and a failure there shouldn't blank out the Budget Lines editor those
    // two calls already feed.
    reloadForecast();
    return Promise.all([
      apiFetch(`/budget-lines?projectId=${projectId}`),
      apiFetch(`/projects/${projectId}/billing-rate`),
    ])
      .then(([lineRows, rateRow]) => {
        setLines(lineRows);
        setRate(rateRow ? Number(rateRow.rate) : null);
      })
      .catch((err) => setError('Could not load budget — ' + err.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    setLoading(true);
    apiFetch('/teams').then(setTeams).catch(() => {});
    apiFetch('/budget-categories').then(setCategories).catch(() => {});
    reload();
  }, [projectId, reload]);

  // Editing Planned Hours recalculates Planned $ from the current billing rate -- the whole point
  // of item 6 (Jeremy, 2026-09-28). Applies to project-level lines too (2026-09-28 follow-up): a
  // lump Software/Travel line can carry hours just as well as a labor line can. Only skipped while
  // no rate has been set yet, since there's nothing to multiply by.
  function editCell(id, key, value) {
    setLines((prev) => prev.map((line) => {
      if (line.id !== id) return line;
      const next = { ...line, [key]: value };
      if (key === 'plannedHours' && rate != null) {
        const hrs = Number(value);
        if (Number.isFinite(hrs)) next.plannedAmount = Math.round(hrs * rate * 100) / 100;
      }
      return next;
    }));
    setDirtyIds((prev) => new Set(prev).add(id));
  }

  // Shared by Save and Add Line -- Add Line used to reload the grid out from under any unsaved edits
  // in other rows (Jeremy, 2026-09-28: "save changes made when I click add line"), since reload()
  // replaces `lines` from the server and a dirty row's local-only edit was never sent first.
  async function flushDirty() {
    if (!dirtyIds.size) return;
    await Promise.all([...dirtyIds].map((id) => apiFetch(`/budget-lines/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(buildPayload(lines.find((l) => l.id === id))),
    })));
    setDirtyIds(new Set());
  }

  async function addLine() {
    setBusy('adding');
    setActionError('');
    try {
      await flushDirty();
      const created = await apiFetch('/budget-lines', {
        method: 'POST',
        body: JSON.stringify({ ...BLANK_LINE, projectId, categoryId: categories[0].id }),
      });
      setPinnedId(created.id);
      await reload();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy('');
    }
  }

  async function saveAll() {
    setBusy('saving');
    setActionError('');
    try {
      await flushDirty();
      await reload();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy('');
    }
  }

  async function deleteLine(id) {
    setActionError('');
    try {
      await apiFetch(`/budget-lines/${id}`, { method: 'DELETE' });
      setDirtyIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await reload();
    } catch (err) {
      setActionError(err.message);
    }
  }

  async function handleImportFile(e) {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setActionError('');
    try {
      setImportPreview(await parseEstimateWorkbook(file, categories, teams));
    } catch (err) {
      setActionError('Could not read estimate — ' + err.message);
    }
  }

  async function confirmImport() {
    setBusy('importing');
    setActionError('');
    try {
      await flushDirty();
      for (const l of importPreview.lines) {
        await apiFetch('/budget-lines', {
          method: 'POST',
          body: JSON.stringify({
            projectId, categoryId: l.categoryId, plannedAmount: l.plannedAmount, plannedHours: l.plannedHours,
            ...(l.teamId ? { teamId: l.teamId } : {}),
          }),
        });
      }
      setImportPreview(null);
      await reload();
    } catch (err) {
      setActionError(err.message);
    } finally {
      setBusy('');
    }
  }

  async function handleSetRate(newRate, effectiveDate) {
    await apiFetch(`/projects/${projectId}/billing-rate`, {
      method: 'POST',
      body: JSON.stringify({ rate: newRate, effectiveDate }),
    });
    // A future-dated rate isn't "current" yet, so re-read rather than show it as in effect.
    const row = await apiFetch(`/projects/${projectId}/billing-rate`);
    setRate(row ? Number(row.rate) : null);
  }

  if (loading) return <div style={{ textAlign:'center', padding:32, color:'var(--text-mid)', fontSize:13 }}>Loading…</div>;

  return (
    <div>
      <ErrorBanner error={error} />
      {actionError && <ErrorBanner error={actionError} />}

      <SummaryCards forecast={forecast} />
      <BudgetTimeline projectId={projectId} refreshKey={forecast} />
      <PickupCard projectId={projectId} forecast={forecast} onChange={reloadForecast} />
      <ForecastSection forecast={forecast} projectName={projectName} onOpenMonthly={() => setShowMonthly(true)} />
      <BudgetAdjustmentsCard projectId={projectId} adjustments={adjustments} onChange={reloadForecast} />
      {showMonthly && <MonthlyBreakdownModal projectId={projectId} onClose={() => setShowMonthly(false)} />}
      {importPreview && (
        <ImportPreviewModal
          preview={importPreview} existingCount={lines.length} busy={busy === 'importing'}
          onConfirm={confirmImport}
          onRemove={(i) => setImportPreview((p) => ({ ...p, lines: p.lines.filter((_, j) => j !== i) }))}
          onClose={() => setImportPreview(null)}
        />
      )}

      <div className="card">
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:10 }}>
          <div className="card-title">Budget Lines</div>
          <div style={{ display:'flex', alignItems:'center', gap:16 }}>
            <BillingRateControl rate={rate} onSet={handleSetRate} />
            <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:2 }}>
              <Link to="/billing-rates" style={{ fontSize:12 }}>Bill Rates and History →</Link>
              <Link to="/administration?tab=projects&subtab=budget-categories" style={{ fontSize:12 }}>Manage categories →</Link>
            </div>
          </div>
        </div>

        {categories.length === 0 ? (
          <div style={{ fontSize:13, color:'var(--text-mid)' }}>
            No budget categories configured yet — add some on the Project Configuration page before adding lines.
          </div>
        ) : (
          <>
            <div className="sheet-toolbar">
              <div className="spacer" />
              <input ref={importInput} type="file" accept=".xlsx,.xlsm" style={{ display:'none' }} onChange={handleImportFile} />
              <button className="btn btn-outline" onClick={() => importInput.current.click()} disabled={!!busy}>
                Import estimate…
              </button>
              <button className="btn btn-outline" onClick={addLine} disabled={!!busy}>
                {busy === 'adding' ? 'Adding…' : '+ Add line'}
              </button>
              <button className="btn btn-primary" onClick={saveAll} disabled={!!busy || !dirtyIds.size}>
                {busy === 'saving' ? 'Saving…' : dirtyIds.size ? `Save ${dirtyIds.size} change(s)` : 'Saved'}
              </button>
            </div>
            {lines.length === 0 ? (
              <div style={{ fontSize:13, color:'var(--text-mid)' }}>No budget lines yet — add one above.</div>
            ) : (
              <BudgetLinesGrid
                lines={lines} teams={teams} categories={categories} dirtyIds={dirtyIds}
                sort={sort} onSort={handleSort} onEdit={editCell} onDelete={deleteLine} pinnedId={pinnedId}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function ProjectBudgets() {
  const [projects, setProjects] = useState([]);
  const [ready, setReady] = useState(false);
  const [projectId, setProjectId] = useSelectedProject();

  useEffect(() => {
    apiFetch('/projects')
      .then((rows) => setProjects(rows))
      .finally(() => setReady(true));
  }, []);

  usePruneMissingProject(projectId, setProjectId, projects, ready);

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Professional Services</div>
        <h1 className="page-title">Project Budgets</h1>
        <p className="page-subtitle">Maintain planned vs. actual budget by team and by project — hours and dollars together.</p>
      </div>

      <ProjectPicker projects={projects} value={projectId} onChange={setProjectId} />

      {projectId && <ProjectBudgetDetail projectId={projectId} projectName={projects.find((p) => p.id === projectId)?.name} />}
    </div>
  );
}
